import { Button, Input, Tooltip } from '@heroui/react'
import { PlusCircle, RefreshCw, Trash, Code } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

function PayloadEditor({ 
  value = {}, 
  onChange, 
  testMode = false,
  className = '', 
  scriptVariables, 
  onSyncWithVariables, 
  onGenerateCode,
  onAddFirstVariable,
  title = "Test Payload",
  description = "Define variables to use when testing your script"
}) {
  const [pairs, setPairs] = useState([])

  // Initialize from value prop
  useEffect(() => {
    // Convert object to array of pairs
    if (Object.keys(value).length > 0) {
      const pairsArray = Object.entries(value).map(([key, val]) => ({
        key,
        value: val
      }))
      setPairs(pairsArray)
    } else {
      // Always have at least one empty pair for input
      setPairs([{ key: '', value: '' }])
    }
  }, [value])

  // Handle syncing with script variables
  useEffect(() => {
    if (scriptVariables && Object.keys(scriptVariables).length > 0 && onSyncWithVariables) {
      const syncedPayload = { ...value }
      let hasChanges = false
      
      // Add any script variables that aren't in the payload
      Object.keys(scriptVariables).forEach(key => {
        if (!syncedPayload.hasOwnProperty(key)) {
          syncedPayload[key] = scriptVariables[key]
          hasChanges = true
        }
      })
      
      if (hasChanges) {
        onSyncWithVariables(syncedPayload)
      }
    }
  }, [scriptVariables, value, onSyncWithVariables])

  // Update parent component with changes
  const updateParent = (newPairs) => {
    const payload = {}
    
    // Only include pairs with non-empty keys
    newPairs.forEach(pair => {
      if (pair.key) {
        payload[pair.key] = pair.value
      }
    })
    
    onChange(payload)
  }

  // Handle input changes (for both key and value)
  const handleChange = (index, field, newValue) => {
    const newPairs = [...pairs];
    newPairs[index] = { ...newPairs[index], [field]: newValue };
    
    setPairs(newPairs)
    
    // Delay updating parent to avoid excessive re-renders during typing
    clearTimeout(window[`${field}ChangeTimeout`])
    window[`${field}ChangeTimeout`] = setTimeout(() => {
      updateParent(newPairs)
    }, 300)
  }

  // Add a new pair
  const addPair = () => {
    // If there are no pairs, use the onAddFirstVariable handler if provided
    if (pairs.length === 0 && onAddFirstVariable) {
      onAddFirstVariable()
      return
    }
    
    const newPair = { key: '', value: '' }
    const newPairs = [...pairs, newPair]
    
    setPairs(newPairs)
  }

  // Remove a pair
  const removePair = (index) => {
    const newPairs = [...pairs];
    newPairs.splice(index, 1);
    
    // Always keep at least one pair
    if (newPairs.length === 0) {
      newPairs.push({ key: '', value: '' })
    }
    
    setPairs(newPairs)
    updateParent(newPairs)
  }

  // Sync with script variables
  const handleSync = () => {
    if (scriptVariables && Object.keys(scriptVariables).length > 0) {
      // Create pairs from script variables
      const syncedPairs = Object.entries(scriptVariables).map(([key, val]) => ({
        key,
        value: val
      }))
      
      // Add any existing pairs that aren't in script variables
      pairs.forEach(pair => {
        if (pair.key && !scriptVariables.hasOwnProperty(pair.key)) {
          syncedPairs.push(pair)
        }
      })
      
      // Ensure we have at least one pair
      if (syncedPairs.length === 0) {
        syncedPairs.push({ key: '', value: '' })
      }
      
      setPairs(syncedPairs)
      updateParent(syncedPairs)
    }
  }

  // Generate variable extraction code
  const handleGenerateCode = () => {
    if (typeof onGenerateCode === 'function') {
      // Get all valid variable names (non-empty keys)
      const variableNames = pairs
        .filter(pair => pair.key && pair.key.trim() !== '')
        .map(pair => pair.key.trim())
      
      if (variableNames.length === 0) {
        toast.warning('Please define at least one variable first')
        return
      }
      
      // Make sure to call the function correctly
      onGenerateCode(variableNames)
    } else {
      toast.warning('Generate code functionality is not available')
    }
  }

  return (
    <div className={`${className}`}>
      <div className="flex justify-between items-center mb-2">
        <div className="flex flex-col">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-base font-medium text-gray-800">{title}</h3>
            {testMode && (
              <span className="bg-amber-100 text-amber-800 text-xs px-2 py-0.5 rounded">Test Mode</span>
            )}
          </div>
          <p className="text-sm text-gray-600">
            {description}
          </p>
        </div>
        <div className="flex gap-2 items-center">
          {typeof onGenerateCode === 'function' && (
            <Tooltip content="Generate variable extraction code">
              <Button
                size="sm"
                variant="light"
                color="default"
                isIconOnly
                onClick={handleGenerateCode}
                className="h-8 w-8 min-w-0 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                <Code size={16} className="text-black" />
              </Button>
            </Tooltip>
          )}
          {scriptVariables && Object.keys(scriptVariables).length > 0 && (
            <Tooltip content="Sync with script variables">
              <Button
                size="sm"
                variant="light"
                color="default"
                isIconOnly
                onClick={handleSync}
                className="h-8 w-8 min-w-0 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                <RefreshCw size={16} className="text-gray-600" />
              </Button>
            </Tooltip>
          )}
          <Tooltip content="Add new variable">
            <Button
              size="sm"
              variant="light"
              color="default"
              isIconOnly
              onClick={addPair}
              className="h-8 w-8 min-w-0 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              <PlusCircle size={16} className="text-black" />
            </Button>
          </Tooltip>
        </div>
      </div>

      <div className="space-y-2">
        {pairs.map((pair, index) => (
          <div 
            key={index} 
            className="flex flex-row gap-3 w-full items-center group p-0.5 hover:bg-gray-50 rounded-md transition-colors"
          >
            <Input
              placeholder="Variable name"
              value={pair.key}
              aria-label="Variable name"
              variant="flat"
              size="sm"
              fullWidth
              className="flex-1"
              classNames={{
                inputWrapper: "bg-transparent shadow-none hover:shadow-sm transition-shadow border border-gray-200",
                input: "text-sm"
              }}
              onChange={(e) => handleChange(index, 'key', e.target.value)}
            />
            <Input
              placeholder="Value"
              value={pair.value}
              aria-label="Variable value"
              variant="flat"
              size="sm"
              fullWidth
              className="flex-1"
              classNames={{
                inputWrapper: "bg-transparent shadow-none hover:shadow-sm transition-shadow border border-gray-200",
                input: "text-sm"
              }}
              onChange={(e) => handleChange(index, 'value', e.target.value)}
            />
            <Tooltip content="Remove">
              <Button
                size="sm"
                variant="light"
                color="default"
                isIconOnly
                onClick={() => removePair(index)}
                className="h-8 w-8 min-w-0 opacity-0 group-hover:opacity-100 transition-opacity rounded-full"
                isDisabled={pairs.length <= 1} // Don't allow removing if it's the last pair
              >
                <Trash size={16} className="text-gray-700" />
              </Button>
            </Tooltip>
          </div>
        ))}
      </div>
    </div>
  )
}

export default PayloadEditor